// AuthContext — reads the caller's identity from /api/me (which the
// topograph backend gates on the topograph_session cookie). The JWT itself
// is HttpOnly, so the SPA never touches it directly; login/logout are
// browser navigations to /api/login and /api/logout.
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";

interface MeResponse {
    email: string;
    name?: string;
    scopes?: string[];
}

interface AuthState {
    email: string | null;
    name: string | null;
    scopes: string[];
    loading: boolean;
    loggedIn: boolean;
    login: (next?: string) => void;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [email, setEmail] = useState<string | null>(null);
    const [name, setName] = useState<string | null>(null);
    const [scopes, setScopes] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const resp = await fetch("/api/me", { credentials: "include" });
            if (!resp.ok) {
                setEmail(null);
                setName(null);
                setScopes([]);
                return;
            }
            const data: MeResponse = await resp.json();
            setEmail(data.email ?? null);
            setName(data.name ?? null);
            setScopes(data.scopes ?? []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Sign-in goes to the SPA's own /login page rather than straight to
    // /api/login. That page hosts the form when the backend can take a
    // password, and re-issues this exact redirect when it can't — so the
    // pre-embedded-login behavior is still one hop away, not lost.
    const login = useCallback((next?: string) => {
        const target = next ?? window.location.pathname + window.location.search;
        const q = target ? `?next=${encodeURIComponent(target)}` : "";
        window.location.href = `/login${q}`;
    }, []);

    const logout = useCallback(async () => {
        await fetch("/api/logout", { method: "POST", credentials: "include" });
        setEmail(null);
        setName(null);
        setScopes([]);
    }, []);

    const value = useMemo<AuthState>(() => ({
        email,
        name,
        scopes,
        loading,
        loggedIn: email !== null,
        login,
        logout,
        refresh,
    }), [email, name, scopes, loading, login, logout, refresh]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
}
